import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  OnboardingStoreType,
  OracleOnboardingSectionKey
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { useSetError } from '@src/redux/modals';
import { uploadSourceDocument } from '../onboarding-api';
import { useEditorName } from '../useEditorName';
import { onboardingSectionQueryKey } from '../useOnboardingSections';
import { AddDataSourceModal } from './AddDataSourceModal';

interface AddSourceFlowProps {
  company: string;
  store: OnboardingStoreType;
  sectionKey: OracleOnboardingSectionKey;
  onClose: () => void;
}

// Rendered only while a section is taking a source, so `sectionKey` is always a real section.
export const AddSourceFlow = ({ company, store, sectionKey, onClose }: AddSourceFlowProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const setError = useSetError();
  const queryClient = useQueryClient();
  const userFullName = useEditorName();

  const handleSubmit = async (file: File) => {
    setIsUploading(true);
    try {
      await uploadSourceDocument({ company, store, sectionKey, file, editorName: userFullName });
      onClose();
    } catch (e) {
      setError({ message: e.message, reportError: e });
      return;
    } finally {
      setIsUploading(false);
    }

    // The version is already committed, so a failed refetch must not report as a failed
    // upload; the card's poll picks it up either way.
    await queryClient.invalidateQueries({
      queryKey: onboardingSectionQueryKey(company, sectionKey, store)
    });
  };

  return (
    <AddDataSourceModal
      show
      onHide={() => !isUploading && onClose()}
      onSubmit={handleSubmit}
      isSubmitting={isUploading}
    />
  );
};
